import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Tooltip from '@mui/material/Tooltip';
import { useNavigate } from '@tanstack/react-router';
import { type MouseEvent, type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  FREE_SUBSCRIPTION_LIMITS,
  type FreeSubscriptionLimit,
  hasReachedFreeSubscriptionLimit,
  isFreeSubscription,
} from '@/config/subscription-limits';
import { useUserSession } from '@/context/user-session-context';

export function useFreeSubscriptionLimit(limit: FreeSubscriptionLimit, currentCount: number) {
  const { t } = useTranslation();
  const { user } = useUserSession();
  const reached = isFreeSubscription(user?.planId)
    && hasReachedFreeSubscriptionLimit(limit, currentCount);
  const maximum = FREE_SUBSCRIPTION_LIMITS[limit];
  const message = t(`subscription_limits.messages.${limit}`, { maximum });

  return { reached, maximum, message };
}

interface SubscriptionLimitDialogProps {
  open: boolean;
  message: string;
  onClose: () => void;
}

export function SubscriptionLimitDialog({ open, message, onClose }: SubscriptionLimitDialogProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('subscription_limits.title')}</DialogTitle>
      <DialogContent>
        <DialogContentText>{message}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('subscription_limits.ok')}</Button>
        <Button
          variant="contained"
          onClick={() => {
            onClose();
            navigate({ to: '/pricing' });
          }}
        >
          {t('subscription_limits.go_to_subscriptions')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

interface SubscriptionLimitGateProps {
  limit: FreeSubscriptionLimit;
  currentCount: number;
  children: ReactNode;
}

export function SubscriptionLimitGate({ limit, currentCount, children }: SubscriptionLimitGateProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const { reached, message } = useFreeSubscriptionLimit(limit, currentCount);

  const interceptLimitedAction = (event: MouseEvent<HTMLDivElement>) => {
    if (!reached) return;
    event.preventDefault();
    event.stopPropagation();
    setDialogOpen(true);
  };

  return (
    <>
      <Tooltip title={reached ? message : ''} arrow>
        <Box
          component="span"
          aria-disabled={reached || undefined}
          onClickCapture={interceptLimitedAction}
          sx={{
            display: 'inline-flex',
            ...(reached && {
              cursor: 'not-allowed',
              '& .MuiButtonBase-root, & [role="button"]': {
                opacity: 0.5,
                filter: 'grayscale(35%)',
                cursor: 'not-allowed',
              },
            }),
          }}
        >
          {children}
        </Box>
      </Tooltip>
      <SubscriptionLimitDialog
        open={dialogOpen}
        message={message}
        onClose={() => setDialogOpen(false)}
      />
    </>
  );
}
